import { Card, Table } from '@mlc/web-ui-toolkit';
import React from 'react';
import * as Styled from './styled';

type Props = {
	topScrollId: string;
	containerProps: any;
	tableProps: any;
};

type Scrollbars = {
	top: any;
	bottom: any;
};

type ScrollbarDimensions = {
	height: string;
	width: string;
};

const ScrollableTable: React.FunctionComponent<Props> = props => {
	const { topScrollId, containerProps, tableProps } = props;
	const [scrollbars, setScrollbars] = React.useState<Scrollbars>({
		top: undefined,
		bottom: undefined,
	});
	const [topScrollWidth, setTopScrollWidth] = React.useState(0);
	const [areScrollbarsSet, setAreScrollbarsSet] = React.useState(false);
	const [scrollbarMargins, setScrollbarMargins] = React.useState<
		ScrollbarDimensions
	>({
		height: '0',
		width: '0',
	});

	//Windows always takes up space with scrollbars where Mac does not take any space, this really seems hacky though
	const isWindows = navigator.appVersion.indexOf('Windows') > -1;

	//Resizes top scrollbar width to match table/main container on window resize
	const handleWindowResize = () => {
		const topScrollWidth = getWidth(tableProps.id);
		setTopScrollWidth(topScrollWidth);
	};

	//Syncs scrolls of top and main scrollbars
	const handleScroll = (event: any) => {
		let elToScroll =
			event.target === scrollbars.top ? scrollbars.bottom : scrollbars.top;
		elToScroll.scrollLeft = event.target.scrollLeft;
	};

	const getScrollbarMargins = () => {
		const container = scrollbars.bottom;

		let isOverflowY =
			container.scrollHeight > container.clientHeight || isWindows;
		let isOverflowX =
			container.scrollWidth > container.clientWidth || isWindows;

		let margin = (
			scrollbars.bottom.offsetWidth - scrollbars.bottom.clientWidth || 17
		).toString(); // 17px seems to be standard width for scrollbars
		return {
			width: isOverflowY ? margin : '0',
			height: isOverflowX ? margin : '0',
		};
	};

	React.useEffect(() => {
		const setState = async () => {
			handleWindowResize();
			const foundScrollbars = {
				top: document.getElementById(topScrollId),
				bottom: document.getElementById(containerProps.id),
			};
			setScrollbars(foundScrollbars);
			window.addEventListener('resize', handleWindowResize);
			setAreScrollbarsSet(true);
		};

		const setScroll = async () => {
			scrollbars.top.onscroll = handleScroll;
			scrollbars.bottom.onscroll = handleScroll;
			setScrollbarMargins(getScrollbarMargins);
		};

		setState();
		if (areScrollbarsSet) {
			setScroll();
		}

		return () => {
			window.removeEventListener('resize', handleWindowResize);
			if (areScrollbarsSet) {
				scrollbars.top.onscroll = null;
				scrollbars.bottom.onscroll = null;
			}
		};
	}, [areScrollbarsSet]);

	return (
		<React.Fragment>
			{/* Can't add in a scrollbar to the top of the container with pure css so we have to hack in one through JS */}
			<Card
				style={{
					height: `${scrollbarMargins.height}px`,
					overflowX: 'scroll',
					overflowY: 'hidden',
					marginRight: `${scrollbarMargins.width}px`,
				}}
				id={topScrollId}
			>
				<div style={{ height: '100%', width: topScrollWidth }}>&nbsp;</div>
			</Card>
			{/* tabIndex=0 so we can focus and scroll using keyboard controls */}
			<Styled.DoubleScrollContainer
				{...containerProps}
				style={{ overflow: 'scroll', ...containerProps.style }}
				tabIndex={0}
			>
				<Table {...tableProps}>{props.children}</Table>
			</Styled.DoubleScrollContainer>
		</React.Fragment>
	);
};

const getWidth = (elID: any) => {
	let el = document.getElementById(elID);
	return el ? el.getBoundingClientRect().width : 0;
};

export default ScrollableTable;
